<script lang="ts">
  /**
   * "Which room is coldest?" — a ranked list, and deliberately no chart.
   *
   * The question has an answer of the form "this one, then this one". A bar
   * chart of six room temperatures encodes the ordering twice — once in the
   * order of the rows and once in the length of the bars — and adds a length
   * comparison the reader did not ask for. The ranking is the answer; the
   * temperature is its evidence, and it belongs in a column you can read down.
   *
   * Which is why the figures are `.fig`: tabular figures line up on the decimal
   * without an alignment rule, so "18.4" and "9.2" sit under each other the way
   * a column of numbers is supposed to.
   */
  import { fig, nice, type RankedAnswer } from "../answers";

  let { a }: { a: RankedAnswer } = $props();

  const range = (min: number | null, max: number | null) =>
    min === null || max === null ? "—" : `${fig(min)} – ${fig(max)}`;
</script>

<div class="k">
  <ol class="k__list">
    {#each a.rooms as r, i (r.entityId)}
      <li class="k__row" class:k__row--first={i === 0}>
        <span class="k__rank num">{i + 1}</span>
        <span class="k__text">
          <span class="k__nm">{nice(r.entityId)}</span>
          <span class="k__id num">{r.entityId}</span>
        </span>
        <span class="k__range num">
          {range(r.min, r.max)}<span class="k__u">{a.unit} range</span>
        </span>
        <span class="fig k__avg">{fig(r.avg)}<span class="fig__u">{a.unit}</span></span>
      </li>
    {/each}
  </ol>

  <p class="k__foot">
    Mean of each sensor's daily average over the last {a.days} days.
    {#if a.excluded.length > 0}
      <!-- Named rather than silently dropped: "coldest room" that turns out to be
           the garden is a wrong answer, and a reader who cannot see what was left
           out has no way to catch it. -->
      {a.excluded.length}
      {a.excluded.length === 1 ? "sensor was" : "sensors were"} left out as not a room —
      <span class="num">{a.excluded.join(", ")}</span>.
    {/if}
  </p>
</div>

<style>
  .k {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
  .k__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .k__row {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: var(--sp-3);
    min-height: var(--row-h);
    padding: var(--sp-2) 0;
    border-bottom: 1px solid var(--line);
  }
  .k__row:last-child {
    border-bottom: none;
  }
  /* The answer to the question is row one, so row one is heavier. Weight and
     position, not colour: the coldest room is a fact, not a fault. */
  .k__row--first .k__nm,
  .k__row--first .k__avg {
    color: var(--tx);
    font-weight: 800;
  }
  .k__rank {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    text-align: right;
  }
  .k__text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .k__nm {
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--tx2);
    overflow-wrap: anywhere;
  }
  .k__id {
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .k__range {
    font-size: var(--fs-micro);
    color: var(--mut);
    white-space: nowrap;
  }
  .k__u {
    margin-left: 4px;
  }
  .k__avg {
    /* One step down from the hero figure size: this is a column of numbers to be
       compared, not a headline to be read across the room. */
    font-size: var(--fs-h3);
    color: var(--tx2);
  }

  /* At a narrow container the min–max range is the first thing that can go: the
     ranking and the average still answer the question without it. */
  @container panel (max-width: 460px) {
    .k__row {
      grid-template-columns: 22px minmax(0, 1fr) auto;
    }
    .k__range {
      display: none;
    }
  }

  .k__foot {
    margin: 0;
    font-size: var(--fs-micro);
    color: var(--mut);
    line-height: 1.5;
    max-width: 70ch;
  }
</style>
